import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getTranslationsStatic } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";

/**
 * Files pinned to their folder (franklin-data-pipeline `pinned_documents` /
 * `guard_pinned_document`):
 * - `brief_additions`: the brief's Additions PDF — may not move, may be trashed.
 * - `issue_attachment`: a file in Issue Attachments that an issue links — may
 *   neither move nor be trashed; it goes when its last issue lets go of it.
 *
 * The database refuses these whatever the caller; this module lets a move or
 * trash leave them out up front, name each one, and carry on with the rest.
 */
export type PinReason = "brief_additions" | "issue_attachment";

export type PinnedAction = "move" | "trash";

export interface PinnedDocument {
  filename: string;
  reason: PinReason;
}

/** Toasts naming individual files; the rest of a large selection get one count. */
const NAMED_TOAST_LIMIT = 3;

const PIN_HINT_PREFIX = "pinned_document:";

const MISSING_FUNCTION_CODES: ReadonlySet<string> = new Set(["PGRST202", "42883"]);

function isPinReason(value: string): value is PinReason {
  return value === "brief_additions" || value === "issue_attachment";
}

function blocks(reason: PinReason, action: PinnedAction): boolean {
  return action === "move" || reason === "issue_attachment";
}

/**
 * The documents among these that may not leave their folder, with their names
 * and why, keyed by id. Empty before the pipeline migration has applied, when
 * nothing is pinned yet.
 */
export async function fetchPinnedDocuments(
  documentIds: string[]
): Promise<Map<string, PinnedDocument>> {
  const pinned = new Map<string, PinnedDocument>();
  const ids = [...new Set(documentIds)].map(Number).filter(Number.isFinite);
  if (ids.length === 0) return pinned;

  const { data, error } = await supabase.rpc("pinned_documents", { p_document_ids: ids });
  if (error) {
    if (MISSING_FUNCTION_CODES.has(error.code ?? "")) return pinned;
    throw new Error(error.message);
  }
  for (const row of (data ?? []) as Array<{
    document_id: number;
    filename: string;
    reason: string;
  }>) {
    if (isPinReason(row.reason)) {
      pinned.set(String(row.document_id), { filename: row.filename, reason: row.reason });
    }
  }
  return pinned;
}

/**
 * Leaves out of a selection the documents pinned against this action, tells
 * the user about each one, and returns the ids that may go ahead.
 */
export async function withoutPinnedDocuments(
  documentIds: string[],
  action: PinnedAction
): Promise<string[]> {
  const pinned = await fetchPinnedDocuments(documentIds);
  const refused: PinnedDocument[] = [];
  const allowed = documentIds.filter((id) => {
    const doc = pinned.get(id);
    if (!doc || !blocks(doc.reason, action)) return true;
    refused.push(doc);
    return false;
  });
  notifyPinnedDocuments(refused);
  return allowed;
}

/**
 * A document write the database refused because the file is pinned to its
 * folder. Its message is the sentence the user reads, in their language — never
 * the database's own text — so a surface that prints `error.message` names the
 * file and nothing else.
 */
export class PinnedDocumentError extends Error {
  readonly pinned: PinnedDocument;

  constructor(pinned: PinnedDocument) {
    super(pinnedDocumentMessage(pinned));
    this.name = "PinnedDocumentError";
    this.pinned = pinned;
  }
}

/** The pinned file a database refusal names, or null for any other error. */
export function pinnedDocumentFromError(error: unknown): PinnedDocument | null {
  if (error instanceof PinnedDocumentError) return error.pinned;
  if (!error || typeof error !== "object") return null;
  const { hint, details } = error as { hint?: unknown; details?: unknown };
  if (typeof hint !== "string" || !hint.startsWith(PIN_HINT_PREFIX)) return null;
  const reason = hint.slice(PIN_HINT_PREFIX.length);
  if (!isPinReason(reason)) return null;
  return { filename: typeof details === "string" ? details : "", reason };
}

/**
 * What a refused document write throws: a pinned refusal as a
 * {@link PinnedDocumentError}, anything else with the server's own message.
 */
export function documentWriteError(error: { message: string }): Error {
  const pinned = pinnedDocumentFromError(error);
  return pinned ? new PinnedDocumentError(pinned) : new Error(error.message);
}

/** "“file” can't be removed from the <folder> folder.", in the UI language. */
export function pinnedDocumentMessage({ filename, reason }: PinnedDocument): string {
  const t = getTranslationsStatic();
  const folder =
    reason === "brief_additions" ? t.brief.title : t.documents.folders.issueAttachments;
  return fill(t.documents.folders.pinned.cannotRemove, { filename, folder });
}

/** One toast per pinned file (above the chat pill, like every toast). */
export function notifyPinnedDocuments(pinned: PinnedDocument[]): void {
  if (pinned.length === 0) return;
  const copy = getTranslationsStatic().documents.folders.pinned;

  pinned.slice(0, NAMED_TOAST_LIMIT).forEach((doc) => {
    toast.error(pinnedDocumentMessage(doc), {
      description:
        doc.reason === "issue_attachment" ? copy.issueAttachmentHint : copy.copyInsteadHint,
    });
  });

  const rest = pinned.length - NAMED_TOAST_LIMIT;
  if (rest > 0) toast.error(fill(copy.moreCannotRemove, { count: String(rest) }));
}

/**
 * Tells the user about a write refused for a pinned file and reports whether
 * it was one — so a catch can stop before its generic failure toast.
 */
export function notifyIfPinned(error: unknown): boolean {
  const pinned = pinnedDocumentFromError(error);
  if (!pinned) return false;
  notifyPinnedDocuments([pinned]);
  return true;
}
